import { useState, useEffect } from 'react';
import api from '../services/api';
import { getErrorMessage } from '../services/errorMessages';
import { FiPlus, FiTrash2, FiX, FiUsers, FiSearch, FiEdit2, FiKey, FiSlash } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import './Pages.css';

const Users = () => {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ personal_number: '', first_name: '', last_name: '', password: '', role: 'user' });
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ personal_number: '', first_name: '', last_name: '' });
  const [editError, setEditError] = useState('');

  // Reset password modal
  const [showReset, setShowReset] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Assign modal state
  const [showAssign, setShowAssign] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [assignSearch, setAssignSearch] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch {
      // fetch failed silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/users', form);
      setShowModal(false);
      setForm({ personal_number: '', first_name: '', last_name: '', password: '', role: 'user' });
      fetchUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDelete = (id) => {
    setConfirmAction({
      title: 'თანამშრომლის წაშლა',
      message: 'ნამდვილად გსურთ ამ თანამშრომლის წაშლა? წაიშლება ამ თანამშრომლის ყველა მონაცემი: სამუშაო სესიები, შევსებული დანართები, მიმაგრებული ბენეფიციარები და ისტორია. ეს მოქმედება შეუქცევადია.',
      type: 'delete',
      onConfirm: async () => {
        try { await api.delete(`/users/${id}`); fetchUsers(); } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const handleBlock = (user) => {
    const action = user.is_blocked ? 'განბლოკვა' : 'დაბლოკვა';
    setConfirmAction({
      title: `თანამშრომლის ${action}`,
      message: user.is_blocked
        ? `ნამდვილად გსურთ ${user.first_name} ${user.last_name}-ის განბლოკვა?`
        : `ნამდვილად გსურთ ${user.first_name} ${user.last_name}-ის დაბლოკვა? დაბლოკილი თანამშრომელი ვეღარ შეძლებს სისტემაში შესვლას.`,
      type: user.is_blocked ? 'warning' : 'delete',
      onConfirm: async () => {
        try { await api.put(`/users/${user.id}/block`); fetchUsers(); } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const openAssign = async (user) => {
    setSelectedUser(user);
    setAssignSearch('');
    setAssignLoading(true);
    setShowAssign(true);
    try {
      const [clientsRes, assignedRes] = await Promise.all([
        api.get('/clients'),
        api.get(`/user-clients/user/${user.id}`),
      ]);
      setAllClients(clientsRes.data);
      setAssignedIds(new Set(assignedRes.data.map((c) => c.id)));
    } catch {
      // fetch failed silently
    } finally {
      setAssignLoading(false);
    }
  };

  const toggleClient = (clientId) => {
    setAssignedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const saveAssignment = async () => {
    try {
      await api.post(`/user-clients/user/${selectedUser.id}`, {
        clientIds: Array.from(assignedIds),
      });
      setShowAssign(false);
    } catch {
      // save failed silently
    }
  };

  // Edit user
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ personal_number: u.personal_number, first_name: u.first_name, last_name: u.last_name });
    setEditError('');
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    try {
      await api.put(`/users/${editUser.id}`, editForm);
      setShowEdit(false);
      fetchUsers();
    } catch (err) {
      setEditError(getErrorMessage(err));
    }
  };

  // Reset password
  const openReset = (u) => {
    setResetUser(u);
    setNewPassword('');
    setResetError('');
    setResetSuccess('');
    setShowReset(true);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError('');
    if (newPassword.length < 4) { setResetError('მინიმუმ 4 სიმბოლო'); return; }
    try {
      await api.put(`/users/${resetUser.id}/reset-password`, { new_password: newPassword });
      setResetSuccess('პაროლი შეიცვალა');
      setTimeout(() => setShowReset(false), 1200);
    } catch (err) {
      setResetError(getErrorMessage(err));
    }
  };

  const filteredClients = allClients.filter((c) =>
    `${c.first_name} ${c.last_name} ${c.personal_number}`.toLowerCase().includes(assignSearch.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>თანამშრომლები</h1>
          <p className="page-subtitle">თანამშრომლების მართვა</p>
        </div>
        <button className="btn-primary" onClick={() => { setForm({ personal_number: '', first_name: '', last_name: '', password: '', role: 'user' }); setError(''); setShowModal(true); }}>
          <FiPlus /> დამატება
        </button>
      </div>

      {loading ? (
        <div className="loading">იტვირთება...</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="cards-grid">
            {users.map((u) => (
              <div key={u.id} className="data-card" style={u.is_blocked ? {opacity: 0.6} : {}}>
                <div className="card-header-row">
                  <div className="card-avatar">{u.first_name[0]}{u.last_name[0]}</div>
                  <div>
                    <h3>{u.first_name} {u.last_name}</h3>
                    <span className="card-pn">{u.personal_number}</span>
                  </div>
                  <div style={{marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center'}}>
                    {u.is_blocked && <span className="badge badge-red">დაბლოკილი</span>}
                    <span className={`badge ${u.role === 'superadmin' ? 'badge-gold' : u.role === 'admin' ? 'badge-purple' : 'badge-blue'}`}>
                      {u.role === 'superadmin' ? 'OWNER' : u.role === 'admin' ? 'ადმინისტრატორი' : 'თანამშრომელი'}
                    </span>
                  </div>
                </div>
                <div className="card-actions">
                  <button className="btn-icon btn-edit" onClick={() => openEdit(u)} title="რედაქტირება"><FiEdit2 /></button>
                  <button className="btn-icon" style={{color:'#f59e0b'}} onClick={() => openReset(u)} title="პაროლის რესეტი"><FiKey /></button>
                  {u.role === 'user' && <button className="btn-icon btn-edit" onClick={() => openAssign(u)} title="მიმაგრება"><FiUsers /></button>}
                  <button className="btn-icon" style={{color: u.is_blocked ? '#10b981' : '#ef4444'}} onClick={() => handleBlock(u)} title={u.is_blocked ? 'განბლოკვა' : 'დაბლოკვა'}><FiSlash /></button>
                  <button className="btn-icon btn-danger" onClick={() => handleDelete(u.id)} title="წაშლა"><FiTrash2 /></button>
                </div>
              </div>
            ))}
            {users.length === 0 && <div className="empty-state-card">თანამშრომლები არ არის</div>}
          </div>

          {/* Desktop table */}
          <div className="table-container desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>პირადი ნომერი</th>
                  <th>სახელი</th>
                  <th>გვარი</th>
                  <th>როლი</th>
                  <th>მოქმედება</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={u.is_blocked ? {opacity: 0.6} : {}}>
                    <td>{u.personal_number}</td>
                    <td>{u.first_name}</td>
                    <td>{u.last_name}</td>
                    <td>
                      <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                        {u.is_blocked && <span className="badge badge-red">დაბლოკილი</span>}
                        <span className={`badge ${u.role === 'superadmin' ? 'badge-gold' : u.role === 'admin' ? 'badge-purple' : 'badge-blue'}`}>
                          {u.role === 'superadmin' ? 'OWNER' : u.role === 'admin' ? 'ადმინისტრატორი' : 'თანამშრომელი'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="btn-icon btn-edit" onClick={() => openEdit(u)} title="რედაქტირება"><FiEdit2 /></button>
                        <button className="btn-icon" style={{color:'#f59e0b'}} onClick={() => openReset(u)} title="პაროლის რესეტი"><FiKey /></button>
                        {u.role === 'user' && <button className="btn-icon btn-edit" onClick={() => openAssign(u)} title="მიმაგრება"><FiUsers /></button>}
                        <button className="btn-icon" style={{color: u.is_blocked ? '#10b981' : '#ef4444'}} onClick={() => handleBlock(u)} title={u.is_blocked ? 'განბლოკვა' : 'დაბლოკვა'}><FiSlash /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(u.id)} title="წაშლა"><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan="5" className="empty-state">თანამშრომლები არ არის</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create user modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>ახალი თანამშრომელი</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <label>პირადი ნომერი</label>
                <input inputMode="numeric" pattern="[0-9]*" value={form.personal_number} onChange={(e) => setForm({ ...form, personal_number: e.target.value.replace(/\D/g, '') })} placeholder="შეიყვანეთ პირადი ნომერი" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>სახელი</label>
                  <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="სახელი" required />
                </div>
                <div className="form-group">
                  <label>გვარი</label>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="გვარი" required />
                </div>
              </div>
              <div className="form-group">
                <label>პაროლი</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="მინ. 4 სიმბოლო" required />
              </div>
              {isSuperAdmin && (
                <div className="form-group">
                  <label>როლი</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="user">თანამშრომელი</option>
                    <option value="admin">ადმინისტრატორი</option>
                  </select>
                </div>
              )}
              <button type="submit" className="btn-primary btn-full" disabled={!form.personal_number || !form.first_name.trim() || !form.last_name.trim() || form.password.length < 4}>დამატება</button>
            </form>
          </div>
        </div>
      )}

      {/* Assign clients modal */}
      {showAssign && (
        <div className="modal-overlay" onClick={() => setShowAssign(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>მიმაგრება: {selectedUser?.first_name} {selectedUser?.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowAssign(false)}><FiX /></button>
            </div>
            <div className="assign-content">
              <div className="assign-search-wrap">
                <FiSearch className="assign-search-icon" />
                <input
                  className="assign-search"
                  placeholder="ბენეფიციარის ძიება..."
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                />
              </div>
              <div className="assign-count">
                არჩეულია: <strong>{assignedIds.size}</strong> ბენეფიციარი
              </div>
              {assignLoading ? (
                <div className="loading">იტვირთება...</div>
              ) : (
                <div className="client-checklist">
                  {filteredClients.map((c) => (
                    <label key={c.id} className={`checklist-item ${assignedIds.has(c.id) ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={assignedIds.has(c.id)}
                        onChange={() => toggleClient(c.id)}
                      />
                      <div className="checklist-avatar">{c.first_name[0]}{c.last_name[0]}</div>
                      <div className="checklist-info">
                        <span className="checklist-name">{c.first_name} {c.last_name}</span>
                        <span className="checklist-pn">{c.personal_number}</span>
                      </div>
                    </label>
                  ))}
                  {filteredClients.length === 0 && (
                    <div className="empty-state">ბენეფიციარები არ მოიძებნა</div>
                  )}
                </div>
              )}
              <div className="assign-footer">
                <button className="btn-secondary" onClick={() => setShowAssign(false)}>გაუქმება</button>
                <button className="btn-primary" onClick={saveAssignment}>შენახვა</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {showEdit && editUser && (
        <div className="modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>რედაქტირება: {editUser.first_name} {editUser.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowEdit(false)}><FiX /></button>
            </div>
            <form onSubmit={handleEdit} className="modal-form">
              {editError && <div className="error-message">{editError}</div>}
              <div className="form-group">
                <label>პირადი ნომერი</label>
                <input inputMode="numeric" pattern="[0-9]*" value={editForm.personal_number} onChange={(e) => setEditForm({ ...editForm, personal_number: e.target.value.replace(/\D/g, '') })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>სახელი</label>
                  <input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>გვარი</label>
                  <input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} required />
                </div>
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={!editForm.personal_number || !editForm.first_name.trim() || !editForm.last_name.trim()}>შენახვა</button>
            </form>
          </div>
        </div>
      )}

      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}

      {/* Reset password modal */}
      {showReset && resetUser && (
        <div className="modal-overlay" onClick={() => setShowReset(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>პაროლის რესეტი: {resetUser.first_name} {resetUser.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowReset(false)}><FiX /></button>
            </div>
            <form onSubmit={handleReset} className="modal-form">
              {resetError && <div className="error-message">{resetError}</div>}
              {resetSuccess && <div className="success-message">{resetSuccess}</div>}
              <div className="form-group">
                <label>ახალი პაროლი</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="მინ. 4 სიმბოლო" required />
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={newPassword.length < 4}>პაროლის შეცვლა</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
